import { ColumnActionsMode } from '@fluentui/react';
import { ArticleList, DownloadList, SupercedenceList } from '../../articleList';
import {
  CopyColumn,
  CveLinkColumn,
  DateColumn,
  CweLinkColumn,
} from '../../Search/SearchDetailsList/column';
import { AssigningCnaColumn } from 'component/Search/SearchDetailsList/column/assigningCna';
import { ObjectFieldList } from 'component/articleList/objectFieldList';
import { ISearchListColumn } from 'component/Search/types';
import type { AffectedProduct } from 'types';
import { ISearchListColumnConfig } from 'component/Search/IColumnExtensionType';
import { ReactNode } from 'react';
import { ActionRequiredColumn } from 'component/Search/SearchDetailsList/column/actionRequiredColumn';

export const defaultColumnKeyList = [
  'releaseDate',
  'product',
  'platform',
  'kbArticles',
  'download',
  'cveNumber',
  'severity',
  'impact',
  'fixedBuildNumber',
];
/** Columns for when the app first opens (before user has
 * specified any columns)
 */
export const allUpdateColumnList: ISearchListColumnConfig<AffectedProduct>[] = [
  { key: 'releaseDate', data: { renderType: 'date' } },
  // { key: 'latestRevisionDate', data: { isDate: true } },
  { key: 'productFamily', isMultiline: true },
  { key: 'product', isMultiline: true },
  { key: 'platform', isMultiline: true },
  {
    key: 'impact',
    minWidth: 73,
    maxWidth: 120,
    isMultiline: true,
  },
  {
    key: 'severity',
    minWidth: 73,
    maxWidth: 120,
    isMultiline: true,
  },
  {
    name: 'Article',
    key: 'kbArticles',
    minWidth: 75,
    maxWidth: 90,
    columnActionsMode: ColumnActionsMode.disabled,
    data: {
      renderType: 'list',
      listFieldName: 'articleName',
      listItemColumnData: {
        renderType: 'link',
        linkFieldName: 'articleUrl',
      },
    },
  },
  {
    key: 'supercedence',
    minWidth: 65,
    maxWidth: 90,
    columnActionsMode: ColumnActionsMode.disabled,
    fieldName: 'kbArticles',
    data: {
      renderType: 'list',
      //this is actually capitalized in the data
      listFieldName: 'supercedence',
    },
  },
  {
    key: 'download',
    minWidth: 90,
    maxWidth: 120,
    columnActionsMode: ColumnActionsMode.disabled,
    fieldName: 'kbArticles',
    data: {
      renderType: 'list',
      listFieldName: 'downloadName',
      listItemColumnData: {
        renderType: 'link',
        linkFieldName: 'downloadUrl',
      },
    },
  },
  {
    key: 'fixedBuildNumber',
    fieldName: 'kbArticles',
    name: 'Build Number',
    columnActionsMode: ColumnActionsMode.disabled,
    minWidth: 100,
    maxWidth: 500,
    data: {
      renderType: 'list',
      listFieldName: 'fixedBuildNumber',
    },
  },
  {
    key: 'cveNumber',
    fieldName: 'cveNumber',
    name: 'Details',
    minWidth: 100,
    maxWidth: 120,
    data: {
      renderType: 'link',
      getLink: item => {
        if (!item?.cveNumber) {
          return false;
        }

        return `${window.location.origin}${process.env.PUBLIC_URL}/vulnerability/${item?.cveNumber}`;
      },
    },
  },
  { key: 'baseScore', minWidth: 60, maxWidth: 120 },
  { key: 'temporalScore', minWidth: 60, maxWidth: 120 },
  {
    key: 'vectorString',
    minWidth: 410,
    maxWidth: 600,

    onRender: (item: AffectedProduct, _i, column) => {
      if (column) {
        return <CopyColumn item={item} column={column} />;
      }
      return '-';
    },
  },
  {
    key: 'issuingCna',
    fieldName: 'issuingCna',
    name: 'Assigning CNA',
    minWidth: 73,
    maxWidth: 120,
  },
  {
    key: 'cweList',
    fieldName: 'cweList',
    name: 'CWE',
    // disables sort for CWE column. We may want to add sorting for both cweList and cweDetailsList in the future
    columnActionsMode: ColumnActionsMode.disabled,
    minWidth: 250,
    maxWidth: 600,
  },
  {
    key: 'customerActionRequired',
    fieldName: 'customerActionRequired',
    name: 'Customer Action Required',
  },
];

/** This handles custom item column types for the details list
 */
export const onRenderUpdateListColumn = (
  item: AffectedProduct,
  _index: number | undefined,
  column: ISearchListColumn | undefined
): ReactNode => {
  if (column) {
    switch (column.key) {
      case 'cveNumber':
        return <CveLinkColumn item={item} column={column} />;
      case 'releaseDate':
      case 'latestRevisionDate':
      case 'initialReleaseDate':
        return <DateColumn item={item} column={column} />;
      case 'vectorString':
        return <CopyColumn item={item} column={column} />;
      case 'download':
        return <DownloadList list={item.kbArticles} />;
      case 'kbArticles':
        return <ArticleList list={item.kbArticles} />;
      case 'supercedence':
        return <SupercedenceList articles={item.kbArticles} />;
      case 'fixedBuildNumber':
        return (
          <ObjectFieldList
            articles={item.kbArticles}
            fieldName='fixedBuildNumber'
          />
        );
      case 'issuingCna':
        return <AssigningCnaColumn item={item} column={column} />;
      case 'cweList':
        return <CweLinkColumn item={item} column={column} />;
      case 'customerActionRequired':
        return <ActionRequiredColumn item={item} column={column} />;
      default:
        return column.fieldName && item[column.fieldName]
          ? item[column.fieldName]
          : '-';
    }
  }
  return '-';
};
